import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { measurementAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const MeasurementList = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadMeasurements();
  }, []);

  const loadMeasurements = async () => {
    try {
      const response = await measurementAPI.getAll();
      setMeasurements(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredMeasurements = measurements.filter(measurement =>
    measurement.customer?.name.toLowerCase().includes(searchCustomer.toLowerCase())
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMeasurements.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMeasurements.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('measurements.title')}</h2>
          <p className="text-gray-600 mt-1">{t('measurements.subtitle')}</p>
        </div>
        <div className="w-80">
          <input
            type="text"
            placeholder={t('measurements.searchByCustomer')}
            value={searchCustomer}
            onChange={(e) => setSearchCustomer(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-1">{t('common.status')}</div>
            <div className="col-span-3">{t('salesOrders.customer')}</div>
            <div className="col-span-4">{t('measurements.bodyMeasurements')}</div>
            <div className="col-span-2">{t('auditLogs.user')}</div>
            <div className="col-span-1">{t('common.date')}</div>
            <div className="col-span-1 text-right">{t('common.actions')}</div>
          </div>

          {/* Card Items */}
          {currentItems.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {searchCustomer ? t('measurements.noMeasurementsCustomer') : t('measurements.noMeasurements')}
            </div>
          ) : (
            currentItems.map((measurement) => (
              <div key={measurement.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
                <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  {/* Status */}
                  <div className="col-span-1 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>

                  {/* Customer & Order */}
                  <div className="col-span-3">
                    <div className="text-sm font-semibold text-gray-900">{measurement.customer?.name || 'N/A'}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                          {measurement.sales_order ? (
                        <Link
                          to={`/sales-orders/${measurement.sales_order.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {t('salesOrders.orderNumber')} {measurement.sales_order.order_number}
                        </Link>
                      ) : (
                        t('common.notStarted')
                      )}
                    </div>
                  </div>

                  {/* Measurements */}
                  <div className="col-span-4">
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">{t('measurements.length').charAt(0)}:</span>
                        <span className="ml-1 text-gray-900 font-medium">{measurement.length || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('measurements.chest').charAt(0)}:</span>
                        <span className="ml-1 text-gray-900 font-medium">{measurement.chest || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('measurements.waist').charAt(0)}:</span>
                        <span className="ml-1 text-gray-900 font-medium">{measurement.waist || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('measurements.hips').charAt(0)}:</span>
                        <span className="ml-1 text-gray-900 font-medium">{measurement.hips || '-'}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('measurements.shoulder')}: {measurement.shoulder || '-'} | {t('measurements.sleeve')}: {measurement.sleeve || '-'} | {t('measurements.neck')}: {measurement.neck || '-'}
                    </div>
                  </div>

                  {/* Recorded By */}
                  <div className="col-span-2">
                    <div className="text-sm text-gray-700">{measurement.recorder?.full_name || 'N/A'}</div>
                  </div>

                  {/* Date */}
                  <div className="col-span-1">
                    <div className="text-xs text-gray-600">
                      {new Date(measurement.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end">
                    <Link
                      to={`/measurements/${measurement.id}`}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('common.viewDetails')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {filteredMeasurements.length > 0 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
            <div className="text-sm text-gray-700">
              {t('salesOrders.showLabel')} <span className="font-medium">{Math.min(indexOfFirstItem + 1, filteredMeasurements.length)}</span> {t('common.of')}{' '}
              <span className="font-medium">{filteredMeasurements.length}</span> {t('navigation.measurements')}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    currentPage === index + 1
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
              <span className="text-sm text-gray-700 ml-2">
                {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredMeasurements.length)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeasurementList;
